import React, { useRef, useState } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';

interface OverflowTooltipTextProps {
  text: string;
  /** Optional complete value when the visible text is a compact summary. */
  tooltipText?: string;
  /** Applied to the truncating element (width constraint, font, etc.). */
  className?: string;
}

/**
 * Single-line truncated text that shows the full text in a tooltip on hover,
 * but only when the text actually overflows its container.
 */
const OverflowTooltipText: React.FC<OverflowTooltipTextProps> = ({
  text,
  tooltipText,
  className,
}) => {
  const detailText = tooltipText ?? text;
  const hasTooltipDetail = tooltipText !== undefined;
  const hasAdditionalDetail = hasTooltipDetail || detailText !== text;
  const textRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const handleOpenChange = (next: boolean) => {
    const el = textRef.current;
    setOpen(
      next && (hasTooltipDetail || (!!el && el.scrollWidth > el.clientWidth))
    );
  };

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip open={open} onOpenChange={handleOpenChange}>
        <TooltipTrigger asChild>
          <div
            ref={textRef}
            aria-label={hasAdditionalDetail ? detailText : undefined}
            className={className ? `truncate ${className}` : 'truncate'}
            tabIndex={hasAdditionalDetail ? 0 : undefined}
            title={hasAdditionalDetail ? detailText : undefined}
          >
            {text}
          </div>
        </TooltipTrigger>
        <TooltipContent className='max-w-sm whitespace-pre-wrap break-words'>
          {detailText}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default OverflowTooltipText;
